// frontend/src/components/AdminDashboard.jsx - COMPLETE VERSION with Enhanced System Status
import React, { useState, useEffect } from 'react';
import { useNotification } from './NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, XCircle, Clock, MessageCircle, Users, CreditCard, Activity, Server, RefreshCw, LogOut, Bug, DollarSign, Search, Edit3, Trash2, UserCheck, Filter, AlertTriangle } from 'lucide-react';
import { subscribeToQueueUpdates } from '../services/socketService';
import { adminAPI } from '../services/adminAPI';

// Enhanced Stats Card Component with debug info and subtitle
const StatCard = ({ title, value, color, icon, debug, subtitle }) => {
  const colorClasses = {
    yellow: 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30',
    blue: 'text-blue-400 bg-blue-900/20 border-blue-500/30',
    green: 'text-green-400 bg-green-900/20 border-green-500/30',
    purple: 'text-purple-400 bg-purple-900/20 border-purple-500/30',
    red: 'text-red-400 bg-red-900/20 border-red-500/30'
  };

  return (
    <div className={`bg-gray-800 border rounded-lg p-6 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <p className={`text-3xl font-bold ${colorClasses[color].split(' ')[0]}`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
          {debug && (
            <p className="text-xs text-gray-500 mt-1">{debug}</p>
          )}
        </div>
        <div className={colorClasses[color].split(' ')[0]}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Simplified System Status Tab Component - Core Services Only (Browser-Safe)
const SystemStatusTab = ({ onRefresh }) => {
  const [systemStatus, setSystemStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(new Date());

  useEffect(() => {
    fetchSystemStatus();
    const interval = setInterval(fetchSystemStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchSystemStatus = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching core system status...');
      
      // Fetch only essential system components
      const [dbStatus, emailStatus] = await Promise.all([
        // Database health check
        fetch('/health').then(r => r.json()).catch(() => ({ 
          status: 'error', 
          mongodb: 'disconnected',
          uptime: 0
        })),
        
        // Email service status
        fetch('/api/email/status', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()).catch(() => ({ 
          success: false, 
          data: { configured: false } 
        }))
      ]);

      console.log('📊 Core system status results:', {
        db: dbStatus,
        email: emailStatus
      });

      setSystemStatus({
        database: {
          mongodb: dbStatus.mongodb || dbStatus.status || 'unknown',
          uptime: dbStatus.uptime || 0
        },
        email: {
          configured: emailStatus.success ? emailStatus.data.configured : false,
          service: emailStatus.data?.service || 'Resend'
        },
        lastUpdated: new Date().toISOString()
      });
      
      setLastCheck(new Date());
      console.log('✅ Core system status updated successfully');
      
    } catch (error) {
      console.error('❌ Error fetching system status:', error);
      setSystemStatus({
        database: { mongodb: 'error', uptime: 0 },
        email: { configured: false, service: 'Unknown' },
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'ok' || status === 'connected' || status === true) {
      return 'text-green-400 bg-green-900/20 border-green-500/30';
    }
    if (status === 'error' || status === 'disconnected' || status === false) {
      return 'text-red-400 bg-red-900/20 border-red-500/30';
    }
    return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
  };

  const getStatusIcon = (status) => {
    if (status === 'ok' || status === 'connected' || status === true) {
      return <CheckCircle className="w-6 h-6 text-green-400" />;
    }
    if (status === 'error' || status === 'disconnected' || status === false) {
      return <XCircle className="w-6 h-6 text-red-400" />;
    }
    return <AlertTriangle className="w-6 h-6 text-yellow-400" />;
  };

  const isAllSystemsOk = () => {
    if (!systemStatus.database || !systemStatus.email) return false;
    
    const dbOk = systemStatus.database.mongodb === 'connected';
    const emailOk = systemStatus.email.configured;
    
    return dbOk && emailOk;
  };

  const handleRefresh = async () => {
    console.log('🔄 Refreshing system status...');
    await fetchSystemStatus();
    if (onRefresh) onRefresh();
  };

  return (
    <div className="space-y-6">
      {/* Overall Status Card */}
      <div className={`rounded-xl p-6 border ${isAllSystemsOk() ? 
        'bg-green-900/10 border-green-500/30' : 
        'bg-red-900/10 border-red-500/30'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {isAllSystemsOk() ? (
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            ) : (
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h2 className={`text-xl font-bold ${isAllSystemsOk() ? 'text-green-400' : 'text-red-400'}`}>
                {isAllSystemsOk() ? '🟢 Core Systems Operational' : '🔴 System Issues Detected'}
              </h2>
              <p className="text-gray-400">
                Last checked: {lastCheck.toLocaleTimeString()}
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* System Components Grid - 2x2 Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Database Status */}
        <div className={`bg-gray-800 rounded-xl p-6 border ${getStatusColor(systemStatus.database?.mongodb)}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Database</h3>
            {getStatusIcon(systemStatus.database?.mongodb)}
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">MongoDB:</span>
              <span className={getStatusColor(systemStatus.database?.mongodb).split(' ')[0]}>
                {systemStatus.database?.mongodb === 'connected' ? 'Connected' : 
                 systemStatus.database?.mongodb === 'error' ? 'Error' : 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Status:</span>
              <span className={systemStatus.database?.mongodb === 'connected' ? 'text-green-400' : 'text-red-400'}>
                {systemStatus.database?.mongodb === 'connected' ? 'Operational' : 'Issues Detected'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Uptime:</span>
              <span className="text-white">
                {systemStatus.database?.uptime ? `${Math.floor(systemStatus.database.uptime / 60)} min` : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Email Service Status */}
        <div className={`bg-gray-800 rounded-xl p-6 border ${getStatusColor(systemStatus.email?.configured)}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Email Service</h3>
            {getStatusIcon(systemStatus.email?.configured)}
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Service:</span>
              <span className="text-white">{systemStatus.email?.service || 'Resend'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Configured:</span>
              <span className={systemStatus.email?.configured ? 'text-green-400' : 'text-red-400'}>
                {systemStatus.email?.configured ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Status:</span>
              <span className={systemStatus.email?.configured ? 'text-green-400' : 'text-red-400'}>
                {systemStatus.email?.configured ? 'Ready' : 'Not Configured'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Admin Email:</span>
              <span className="text-white text-xs">intcountm@gmail.com</span>
            </div>
          </div>
        </div>

        {/* API Server Status */}
        <div className="bg-gray-800 rounded-xl p-6 border text-green-400 bg-green-900/20 border-green-500/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">API Server</h3>
            <CheckCircle className="w-6 h-6 text-green-400" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Status:</span>
              <span className="text-green-400">Online</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Version:</span>
              <span className="text-white">1.0.0</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Environment:</span>
              <span className="text-white">Production</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Uptime:</span>
              <span className="text-white">Active</span>
            </div>
          </div>
        </div>

        {/* File Storage Status */}
        <div className="bg-gray-800 rounded-xl p-6 border text-green-400 bg-green-900/20 border-green-500/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">File Storage</h3>
            <CheckCircle className="w-6 h-6 text-green-400" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Status:</span>
              <span className="text-green-400">Available</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Type:</span>
              <span className="text-white">Local Storage</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Access:</span>
              <span className="text-green-400">Ready</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Space:</span>
              <span className="text-white">Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* System Overview */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">System Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${systemStatus.database?.mongodb === 'connected' ? 'text-green-400' : 'text-red-400'}`}>
              {systemStatus.database?.mongodb === 'connected' ? '✓' : '✗'}
            </div>
            <div className="text-sm text-gray-400">Database</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${systemStatus.email?.configured ? 'text-green-400' : 'text-red-400'}`}>
              {systemStatus.email?.configured ? '✓' : '✗'}
            </div>
            <div className="text-sm text-gray-400">Email Service</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">✓</div>
            <div className="text-sm text-gray-400">API Server</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">✓</div>
            <div className="text-sm text-gray-400">File Storage</div>
          </div>
        </div>
      </div>

      {/* Debug Information */}
      {systemStatus.error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
          <h4 className="text-red-400 font-semibold mb-2">System Error:</h4>
          <pre className="text-red-300 text-xs overflow-auto">
            {systemStatus.error}
          </pre>
        </div>
      )}
    </div>
  );
};

// Enhanced Payment Verification Card Component with USD conversion display
const PaymentVerificationCard = ({ payment, onVerify, isProcessing }) => {
  const [notes, setNotes] = useState('');

  const handleApprove = async () => {
    await onVerify(payment._id, true, notes || 'Payment approved by admin');
  };

  const handleReject = async () => {
    if (!notes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    await onVerify(payment._id, false, notes);
  };

  const getStatusIcon = () => {
    switch (payment.status) {
      case 'completed':
        return <CheckCircle className="text-green-400" size={20} />;
      case 'failed':
        return <XCircle className="text-red-400" size={20} />;
      case 'verifying':
        return <Clock className="text-yellow-400" size={20} />;
      default:
        return <Clock className="text-blue-400" size={20} />;
    }
  };

  const getTelegramUrl = () => {
    const message = `Hello! This is regarding your ${payment.planName} payment of ${payment.amount} ${payment.currency}. Your payment has been verified and your subscription is now active. Thank you for choosing Render Studio!`;
    return `https://t.me/intcounters?text=${encodeURIComponent(message)}`;
  };

  const getPaymentMethodDisplay = () => {
    const methodNames = {
      'usdt': 'USDT (TRC-20)',
      'wechat': 'WeChat Pay',
      'alipay': 'Alipay',
      'zalopay': 'ZaloPay',
      'vietqr': 'VietQR Banking',
      'paypal': 'PayPal',
      'crypto': 'Cryptocurrency'
    };
    return methodNames[payment.paymentMethod] || payment.paymentMethod?.toUpperCase() || 'Unknown';
  };

  // Format amount display with USD conversion
  const getAmountDisplay = () => {
    if (payment.currency === 'USD') {
      return `$${payment.amount}`;
    } else if (payment.usdAmount && payment.usdAmount !== payment.amount) {
      // Show original amount + USD equivalent
      return (
        <div>
          <div className="font-semibold">
            {payment.amount} {payment.currency}
          </div>
          <div className="text-sm text-green-400">
            ≈ ${Number(payment.usdAmount).toFixed(2)} USD
          </div>
        </div>
      );
    } else {
      return `${payment.amount} ${payment.currency}`;
    }
  };

  return (
    <div className="p-6 hover:bg-gray-700/50 transition-colors">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Payment Details */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Payment Details</h3>
            <div className="flex items-center space-x-2">
              {getStatusIcon()}
              <span className="text-sm text-gray-300 capitalize">{payment.status}</span>
            </div>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">User:</span>
              <span className="text-white">{payment.user?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Name:</span>
              <span className="text-white">{payment.user?.firstName} {payment.user?.lastName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Plan:</span>
              <span className="text-white font-semibold">{payment.planName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Amount:</span>
              <div className="text-white font-semibold text-right">
                {getAmountDisplay()}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Method:</span>
              <span className="text-white">{getPaymentMethodDisplay()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Transaction:</span>
              <span className="text-white font-mono text-xs break-all">{payment.transactionId}</span>
            </div>
            {payment.country && (
              <div className="flex justify-between">
                <span className="text-gray-400">Country:</span>
                <span className="text-white capitalize">{payment.country}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-400">Submitted:</span>
              <span className="text-white">{new Date(payment.createdAt).toLocaleString()}</span>
            </div>
            {payment.explorerUrl && (
              <div className="flex justify-between">
                <span className="text-gray-400">Explorer:</span>
                <a href={payment.explorerUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs">
                  View Transaction
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Admin Actions */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Admin Actions</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Verification Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes for verification..."
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              rows={3}
            />
          </div>

          {/* Expected Render Allocation */}
          <div className="mb-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
            <h4 className="text-blue-400 font-medium text-sm mb-1">Expected Render Allocation:</h4>
            <p className="text-blue-200 text-sm">
              {{
                Basic: '8 renders/month',
                Pro: '20 renders/month',
                Studio: '35 renders/month'
              }[payment.planName] || '8 renders/month'}
            </p>
          </div>

          <div className="flex space-x-3 mb-4">
            <button
              onClick={handleApprove}
              disabled={isProcessing}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>✓ Approve & Grant Renders</>
              )}
            </button>
            <button
              onClick={handleReject}
              disabled={isProcessing}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg font-medium transition-colors"
            >
              ✗ Reject
            </button>
          </div>

          {/* Telegram Contact Button */}
          <a
            href={getTelegramUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
          >
            <MessageCircle size={16} className="mr-2" />
            Contact User via Telegram
          </a>
        </div>
      </div>
    </div>
  );
};

// Payment Verification Tab Component - Enhanced with USD display
const PaymentVerificationTab = ({ payments, onVerify, processing }) => {
  if (payments.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
        <h3 className="text-xl font-semibold mb-2">All Caught Up!</h3>
        <p className="text-gray-400">No pending payments to verify at the moment.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="p-6 border-b border-gray-700">
        <h2 className="text-xl font-bold text-white">Payment Verification Queue</h2>
        <p className="text-gray-400 text-sm">Review and approve pending payments ({payments.length} pending)</p>
      </div>

      <div className="divide-y divide-gray-700">
        {payments.map((payment) => (
          <PaymentVerificationCard 
            key={payment._id} 
            payment={payment} 
            onVerify={onVerify}
            isProcessing={processing.has(payment._id)}
          />
        ))}
      </div>
    </div>
  );
};

// User Management Tab Component
const UserManagementTab = ({ 
  users, 
  userStats, 
  pagination, 
  loading, 
  search, 
  onSearchChange, 
  sortBy, 
  onSortChange, 
  order, 
  onOrderChange, 
  page, 
  onPageChange, 
  onUpdateRenders, 
  onDeleteUser, 
  onRefresh 
}) => {
  const [editingUser, setEditingUser] = useState(null);
  const [newRenders, setNewRenders] = useState('');

  const handleEditRenders = (user) => {
    setEditingUser(user._id);
    setNewRenders(user.rendersAvailable.toString());
  };

  const handleSaveRenders = async (userId) => {
    await onUpdateRenders(userId, parseInt(newRenders), 'Manual adjustment from user management');
    setEditingUser(null);
    setNewRenders('');
  };

  const getPlanColor = (plan) => {
    const colors = {
      'Free': 'bg-gray-500/20 text-gray-400',
      'Basic': 'bg-blue-500/20 text-blue-400',
      'Pro': 'bg-purple-500/20 text-purple-400',
      'Studio': 'bg-orange-500/20 text-orange-400'
    };
    return colors[plan] || colors['Free'];
  };

  const getActivityStatus = (lastActive) => {
    if (!lastActive) return { status: 'Never', color: 'text-gray-500' };
    
    const now = new Date();
    const active = new Date(lastActive);
    const diffDays = Math.floor((now - active) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return { status: 'Today', color: 'text-green-400' };
    if (diffDays === 1) return { status: 'Yesterday', color: 'text-yellow-400' };
    if (diffDays <= 7) return { status: `${diffDays}d ago`, color: 'text-orange-400' };
    if (diffDays <= 30) return { status: `${diffDays}d ago`, color: 'text-red-400' };
    return { status: '30+ days', color: 'text-gray-500' };
  };

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">User Management</h2>
            <p className="text-gray-400 text-sm">Manage user accounts and render allocations</p>
          </div>
          <button
            onClick={onRefresh}
            className="flex items-center px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </button>
        </div>

        {/* User Stats */}
        {userStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-lg font-bold text-white">{userStats.totalUsers?.toLocaleString() || '0'}</div>
              <div className="text-sm text-gray-400">Total Users</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-lg font-bold text-green-400">{userStats.activeUsers?.toLocaleString() || '0'}</div>
              <div className="text-sm text-gray-400">Active (30d)</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-lg font-bold text-blue-400">{userStats.totalRenders?.toLocaleString() || '0'}</div>
              <div className="text-sm text-gray-400">Total Renders</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-lg font-bold text-purple-400">
                {userStats.averageRenders ? userStats.averageRenders.toFixed(1) : '0.0'}
              </div>
              <div className="text-sm text-gray-400">Avg Renders</div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search users by email or name..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="createdAt">Sort by Join Date</option>
              <option value="lastActive">Sort by Last Active</option>
              <option value="rendersAvailable">Sort by Renders</option>
              <option value="subscriptionPlan">Sort by Plan</option>
            </select>
            <button
              onClick={() => onOrderChange(order === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-white rounded-lg transition-colors"
            >
              {order === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="animate-spin h-8 w-8 text-red-600 mx-auto mb-4" />
            <p className="text-gray-400">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <Users size={48} className="mx-auto mb-4 text-gray-600" />
            <h3 className="text-xl font-semibold mb-2 text-white">No Users Found</h3>
            <p className="text-gray-400">No users match your search criteria.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-700/50">
                <th className="text-left p-4 text-gray-300 font-medium">User</th>
                <th className="text-left p-4 text-gray-300 font-medium">Plan</th>
                <th className="text-left p-4 text-gray-300 font-medium">Renders</th>
                <th className="text-left p-4 text-gray-300 font-medium">Last Active</th>
                <th className="text-left p-4 text-gray-300 font-medium">Joined</th>
                <th className="text-right p-4 text-gray-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {users.map((user) => {
                const activity = getActivityStatus(user.lastActive);
                return (
                  <tr key={user._id} className="hover:bg-gray-700/30">
                    <td className="p-4">
                      <div>
                        <div className="text-white font-medium">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-gray-400 text-sm">{user.email}</div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanColor(user.subscriptionPlan || 'Free')}`}>
                        {user.subscriptionPlan || 'Free'}
                      </span>
                    </td>
                    <td className="p-4">
                      {editingUser === user._id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={newRenders}
                            onChange={(e) => setNewRenders(e.target.value)}
                            className="w-20 px-2 py-1 bg-gray-700 border border-gray-600 text-white rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                            min="0"
                          />
                          <button
                            onClick={() => handleSaveRenders(user._id)}
                            className="text-green-400 hover:text-green-300"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-medium">{user.rendersAvailable || 0}</span>
                          <button
                            onClick={() => handleEditRenders(user)}
                            className="text-gray-400 hover:text-white"
                          >
                            <Edit3 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <span className={`text-sm ${activity.color}`}>
                        {activity.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-gray-400 text-sm">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => onDeleteUser(user._id, user.email)}
                          className="text-red-400 hover:text-red-300 p-1"
                          title="Delete User"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="p-4 border-t border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.currentPage * pagination.limit, pagination.totalUsers)} of{' '}
            {pagination.totalUsers} users
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
            >
              Previous
            </button>
            <span className="text-gray-400 text-sm">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('payments');
  const [pendingPayments, setPendingPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [userPagination, setUserPagination] = useState({});
  const [userSearch, setUserSearch] = useState('');
  const [userSortBy, setUserSortBy] = useState('createdAt');
  const [userOrder, setUserOrder] = useState('desc');
  const [userPage, setUserPage] = useState(1);
  const [stats, setStats] = useState({
    pendingPayments: 0,
    totalUsers: 0,
    completedPayments: 0,
    totalRevenue: 0,
    activeSubscriptions: 0
  });
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [processing, setProcessing] = useState(new Set());
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const { addToast } = useNotification();
  const { t } = useLanguage();

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, userSearch, userSortBy, userOrder, userPage]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const unsubscribe = subscribeToQueueUpdates(token, async (update) => {
      console.log('🔔 Payment queue updated:', update);
      try {
        await fetchData(); // Refresh all data when queue updates
      } catch (err) {
        console.error('Error updating from socket:', err);
      }
    });

    return () => unsubscribe(); // CLEANUP to avoid duplicates
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Admin Dashboard: Fetching data...');
      
      // Fetch both pending payments and stats with enhanced error handling
      const [paymentsResult, statsResult] = await Promise.all([
        adminAPI.getPendingPayments().catch(err => {
          console.error('❌ Failed to fetch pending payments:', err);
          return { success: false, data: [], count: 0, error: err.message };
        }),
        adminAPI.getAdminStats().catch(err => {
          console.error('❌ Failed to fetch admin stats:', err);
          return { 
            success: false, 
            data: {
              pendingPayments: 0,
              totalUsers: 0,
              completedPayments: 0,
              totalRevenue: 0,
              activeSubscriptions: 0
            }, 
            error: err.message 
          };
        })
      ]);

      // Debug logging
      console.log('📊 Admin Dashboard Results:', {
        payments: {
          success: paymentsResult.success,
          count: paymentsResult.count,
          dataLength: paymentsResult.data?.length,
          error: paymentsResult.error
        },
        stats: {
          success: statsResult.success,
          data: statsResult.data,
          error: statsResult.error
        }
      });

      // Set debug info if in debug mode
      if (debugMode) {
        setDebugInfo({
          paymentsResult,
          statsResult,
          timestamp: new Date().toISOString()
        });
      }

      // Update pending payments
      if (paymentsResult.success && Array.isArray(paymentsResult.data)) {
        setPendingPayments(paymentsResult.data);
        console.log(`✅ Set ${paymentsResult.data.length} pending payments`);
      } else {
        console.warn('⚠️ Invalid payments data received:', paymentsResult);
        setPendingPayments([]);
      }

      // Update stats - ensure we always have valid numbers
      if (statsResult.success && statsResult.data) {
        const newStats = {
          pendingPayments: Number(statsResult.data.pendingPayments) || 0,
          totalUsers: Number(statsResult.data.totalUsers) || 0,
          completedPayments: Number(statsResult.data.completedPayments) || 0,
          totalRevenue: Number(statsResult.data.totalRevenue) || 0,
          activeSubscriptions: Number(statsResult.data.activeSubscriptions) || 0
        };
        setStats(newStats);
        console.log('📊 Updated stats:', newStats);
      } else {
        console.warn('⚠️ Invalid stats data received:', statsResult);
        setStats(prev => prev.pendingPayments === 0 ? {
          pendingPayments: 0,
          totalUsers: 0,
          completedPayments: 0,
          totalRevenue: 0,
          activeSubscriptions: 0
        } : prev);
      }

      // Show error notifications if needed
      if (!paymentsResult.success) {
        addToast(`Failed to load pending payments: ${paymentsResult.error}`, 'warning');
      }
      if (!statsResult.success) {
        addToast(`Failed to load dashboard stats: ${statsResult.error}`, 'warning');
      }

    } catch (error) {
      console.error('❌ Admin Dashboard: Critical error fetching data:', error);
      addToast(`Dashboard error: ${error.message}`, 'error');
      
      // Set fallback empty state
      setPendingPayments([]);
      setStats({
        pendingPayments: 0,
        totalUsers: 0,
        completedPayments: 0,
        totalRevenue: 0,
        activeSubscriptions: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for user management
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      console.log('👥 Fetching users for management...');

      const result = await adminAPI.getAllUsers({
        page: userPage,
        limit: 20,
        search: userSearch,
        sortBy: userSortBy,
        order: userOrder
      });

      if (result.success) {
        setUsers(result.data.users);
        setUserStats(result.data.stats);
        setUserPagination(result.data.pagination);
        console.log(`✅ Loaded ${result.data.users.length} users`);
      } else {
        throw new Error(result.error || 'Failed to fetch users');
      }

    } catch (error) {
      console.error('❌ Error fetching users:', error);
      addToast(`Failed to load users: ${error.message}`, 'error');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // Update user renders
  const handleUpdateUserRenders = async (userId, newRenders, reason = '') => {
    try {
      const confirmed = window.confirm(`Are you sure you want to update this user's renders to ${newRenders}?`);
      if (!confirmed) return;

      const result = await adminAPI.updateUserRenders(userId, {
        renders: newRenders,
        reason: reason || 'Manual admin adjustment'
      });

      if (result.success) {
        addToast('User renders updated successfully', 'success');
        fetchUsers(); // Refresh user list
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error updating user renders:', error);
      addToast(`Failed to update renders: ${error.message}`, 'error');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, userEmail) => {
    try {
      const reason = prompt(`Why are you deleting user ${userEmail}? This action cannot be undone.`);
      if (!reason) return;

      const confirmed = window.confirm(`DANGER: This will permanently delete ${userEmail} and all their data. Are you sure?`);
      if (!confirmed) return;

      const result = await adminAPI.deleteUser(userId, { reason });

      if (result.success) {
        addToast('User deleted successfully', 'success');
        fetchUsers(); // Refresh user list
        fetchData(); // Refresh stats
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error deleting user:', error);
      addToast(`Failed to delete user: ${error.message}`, 'error');
    }
  };

  const handleDebugToggle = async () => {
    setDebugMode(!debugMode);
    
    if (!debugMode) {
      try {
        const debugTest = await adminAPI.testAdminConnection();
        console.log('🔧 Debug test result:', debugTest);
        addToast('Debug mode enabled - check console for details', 'info');
      } catch (error) {
        console.error('Debug test failed:', error);
      }
    } else {
      addToast('Debug mode disabled', 'info');
    }
  };

  const handleLogout = async () => {
    try {
      const confirmed = window.confirm('Are you sure you want to sign out?');
      if (!confirmed) return;

      localStorage.removeItem('token');

      if (typeof onLogout === 'function') {
        await onLogout();
        return;
      }

      window.location.href = '/login';
    } catch (err) {
      console.error('Error during logout:', err);
    }
  };

  const handleVerification = async (paymentId, approved, notes = '') => {
    if (processing.has(paymentId)) return;

    try {
      setProcessing(prev => new Set(prev).add(paymentId));
      
      console.log(`🔄 Processing payment verification: ${paymentId}, approved: ${approved}`);
      
      const result = await adminAPI.verifyPayment(paymentId, {
        approved,
        notes: notes || (approved ? 'Approved by admin' : 'Rejected by admin'),
        adminReason: notes
      });

      if (result.success) {
        addToast(
          approved 
            ? `Payment approved! User granted ${result.data?.rendersGranted || 'N/A'} renders`
            : 'Payment rejected successfully',
          approved ? 'success' : 'error'
        );
        
        // Remove from pending list
        setPendingPayments(prev => 
          prev.filter(payment => payment._id !== paymentId)
        );
        
        // Update stats to reflect the change
        setStats(prev => ({
          ...prev,
          pendingPayments: Math.max(0, prev.pendingPayments - 1),
          completedPayments: approved ? prev.completedPayments + 1 : prev.completedPayments
        }));
        
        console.log('✅ Payment processed successfully');
      } else {
        throw new Error(result.error || 'Verification failed');
      }

    } catch (error) {
      console.error('❌ Error processing verification:', error);
      addToast(`Error: ${error.message}`, 'error');
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(paymentId);
        return newSet;
      });
    }
  };

  const tabs = [
    { id: 'payments', name: t('paymentVerification') || 'Payment Verification', icon: CreditCard },
    { id: 'users', name: t('userManagement') || 'User Management', icon: Users },
    { id: 'system', name: t('systemStatus') || 'System Status', icon: Server },
    ...(debugMode ? [{ id: 'debug', name: 'Debug', icon: Bug }] : [])
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-white">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-gray-400">Welcome back, {user?.firstName}!</p>
            {debugMode && (
              <div className="text-xs text-yellow-400 mt-1">
                🔧 DEBUG MODE ACTIVE - Check console for detailed logs
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleDebugToggle}
              className={`flex items-center px-3 py-2 rounded-lg transition-colors ${
                debugMode 
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              <Bug size={16} className="mr-2" />
              {debugMode ? 'Debug ON' : 'Debug'}
            </button>
            <button
              onClick={fetchData}
              className="flex items-center px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              <LogOut size={16} className="mr-2" />
              Sign Out
            </button>
            <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              Admin Panel
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <StatCard 
            title="Pending Payments" 
            value={stats.pendingPayments} 
            color="yellow" 
            icon={<Clock size={24} />}
            debug={debugMode ? `Array length: ${pendingPayments.length}` : null}
          />
          <StatCard title="Total Users" value={stats.totalUsers} color="blue" icon={<Users size={24} />} />
          <StatCard title="Completed Payments" value={stats.completedPayments} color="green" icon={<CheckCircle size={24} />} />
          <StatCard 
            title="Total Revenue" 
            value={
              `${Number(stats.totalRevenue || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`
            } 
            color="purple" 
            icon={<DollarSign size={24} />}
            subtitle="All currencies converted to USD"
          />
          <StatCard title="Active Subscriptions" value={stats.activeSubscriptions} color="green" icon={<Server size={24} />} />
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-gray-800 p-1 rounded-lg">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-red-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <Icon size={18} />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'payments' && (
          <PaymentVerificationTab 
            payments={pendingPayments} 
            onVerify={handleVerification}
            processing={processing}
          />
        )}

        {activeTab === 'users' && (
          <UserManagementTab 
            users={users}
            userStats={userStats}
            pagination={userPagination}
            loading={usersLoading}
            search={userSearch}
            onSearchChange={setUserSearch}
            sortBy={userSortBy}
            onSortChange={setUserSortBy}
            order={userOrder}
            onOrderChange={setUserOrder}
            page={userPage}
            onPageChange={setUserPage}
            onUpdateRenders={handleUpdateUserRenders}
            onDeleteUser={handleDeleteUser}
            onRefresh={fetchUsers}
          />
        )}

        {activeTab === 'system' && (
          <SystemStatusTab onRefresh={fetchData} />
        )}

        {activeTab === 'debug' && debugMode && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-bold mb-4 text-yellow-400">🔧 Debug Information</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-900 p-4 rounded">
                <h4 className="font-semibold mb-2 text-green-400">Current State:</h4>
                <pre className="text-xs text-gray-300 overflow-auto">
                  {JSON.stringify({
                    pendingPaymentsCount: pendingPayments.length,
                    statsData: stats,
                    loading: loading,
                    processingSet: Array.from(processing)
                  }, null, 2)}
                </pre>
              </div>
              
              {debugInfo && (
                <div className="bg-gray-900 p-4 rounded">
                  <h4 className="font-semibold mb-2 text-blue-400">Latest API Responses:</h4>
                  <pre className="text-xs text-gray-300 overflow-auto max-h-64">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </div>
              )}
              
                <button
                  onClick={() => console.log('Current component state:', { pendingPayments, stats, loading })}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded text-sm"
                >
                  Log State to Console
                </button>
              </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;